import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useShouldShowHotelCardClickersContent} from 'components/Accommodation/AccommodationView/Desktop/useShouldShowHotelCardClickersContent'
import {AccommodationViewSuggestedHotels} from 'components/Accommodation/SuggestedHotels'
import {LoginBanner} from 'components/LoginBanner'
import {PriceWatchCTAWrapper} from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper'
import {ScrollSection} from 'components/ScrollSpy'
import {useAccommodationPageScrollTopOffset} from 'hooks/useAccommodationPageScrollTopOffset'
import {useGetRooms} from 'hooks/useGetRooms'
import {useRoomsRefreshToast} from 'hooks/useGetRooms/useRoomsRefreshToast'
import {useIsraeliVatEligibility} from 'hooks/useIsraeliVatEligibility'
import {useOfferCheckErrorToast} from 'hooks/useOfferCheckErrorToast'
import {useShouldShowLoginBanner} from 'hooks/useShouldShowLoginBanner'
import {useShowDatePickerOverlay} from 'hooks/useShowOverlay/useShowOverlay'
import {
  getHasActiveRoomFilter,
  getIsRoomsFiltersLoading,
  isRoomsViewExpanded
} from 'modules/accommodation/selectors'
import {
  markAccommodationPageLoad,
  setRoomsFirstImageLoaded
} from 'modules/accommodation/slice'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getRoomOfferPriceBreakdownOverlay} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {getSearchParameters} from 'modules/sapiSearch/selectors'
import {
  getHotelResult,
  getIsRoomsPollingComplete,
  getIsRoomsPollingWaitingForResults,
  getIsRoomsRefreshing,
  getRoomCardEntities
} from 'modules/searchApi/selectors'
import {RootState} from 'store'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Stack} from '@vio/atlas/helpers/Stack'
import useIsImageloaded from '@vio/atlas/hooks/useIsImageloaded'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import minus from '@vio/atlas/utils/minus'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {Room} from '@vio/core/src/room/types/room'
import useIsomorphicLayoutEffect from '@vio/core/src/utils/react/hooks/useIsomorphicLayoutEffect'
import {PriceBreakdownModal} from '@vio/shared/src/booking/PriceBreakdownModal'
import {getFrozenBookingByItinerary} from '@vio/shared/src/dealFreeze/modules/selector'
import {PriceWatchToggle} from '@vio/shared/src/priceWatch/components/PriceWatchToggle/PriceWatchToggle'
import {PointsPriceToggle} from '@vio/shared/src/whiteLabel/PointsPriceToggle'

import {COMPACT_ROOMS_AMOUNT} from '../constants'
import {RoomCard} from '../RoomCard'
import {RoomOrSplitBooking} from '../RoomCard/RoomOrSplitBooking'
import {RoomsFilters} from '../RoomsFilters'
import {NoFilteredRoomsResults} from '../RoomsFilters/NoFilteredRoomsResults'
import {FiltersSkeleton, RoomsListSkeleton} from './RoomSectionSkeleton'
import {RoomsSectionFooter} from './RoomsSectionFooter'
import {RoomsSectionSearchInputContext} from './RoomsSectionSearchInputContext'
import {RoomsSectionSoldOut} from './RoomsSectionSoldOut'
import {RoomsSectionTitle} from './RoomsSectionTitle'

type PropsTypes = {
  hotelId: string
  hasPriceWatchToggle?: boolean
  /** Wording only, and only the mobile caller sets it. */
  hasAlertText?: boolean
  afterRoomsTitleSlot?: React.ReactNode
}

const RoomsWrapper = styled.div`
  margin-top: ${cssTheme.layout.spacing.s600};
  &.--mobile {
    margin-top: ${cssTheme.layout.spacing.s500};
  }
`

const Wrapper = styled.div<{isMobile?: boolean}>`
  padding-left: ${({isMobile}) =>
    isMobile ? cssTheme.layout.spacing.s400 : cssTheme.layout.spacing.s000};
  padding-right: ${({isMobile}) =>
    isMobile ? cssTheme.layout.spacing.s400 : cssTheme.layout.spacing.s000};
  padding-top: ${({isMobile}) =>
    isMobile ? cssTheme.layout.spacing.s600 : cssTheme.layout.spacing.s000};
  padding-bottom: ${({isMobile}) =>
    isMobile ? cssTheme.layout.spacing.s600 : cssTheme.layout.spacing.s000};
  background-color: ${cssTheme.colors.background.neutral.secondary};
`

const PriceWatchWrapper = styled.div`
  background-color: ${cssTheme.colors.background.neutral.primary};
`

const RoomsFiltersWrapper = styled.div`
  margin-inline: ${minus(cssTheme.layout.spacing.s400)};
  padding-inline-start: ${cssTheme.layout.spacing.s400};
`

const handleScrollTop = () => {
  window.scrollTo({top: 0, behavior: 'smooth'})
}

export const RoomsSection = ({
  hotelId,
  hasPriceWatchToggle,
  hasAlertText,
  afterRoomsTitleSlot
}: PropsTypes) => {
  const dispatch = useDispatch()
  useRoomsRefreshToast()
  useOfferCheckErrorToast()

  const showDatePickerOverlay = useShowDatePickerOverlay()
  const isRoomsFiltersLoading = useSelector(getIsRoomsFiltersLoading)
  const isRoomsPollingWaitingForResults = useSelector(
    getIsRoomsPollingWaitingForResults
  )
  const isRoomsPollingComplete = useSelector(getIsRoomsPollingComplete)
  const {
    rooms,
    availableFilters,
    isLoading,
    isFetching,
    isUninitialized,
    totalResults
  } = useGetRooms()
  const {isMobile} = useDeviceLayout()
  const [hasSimilarHotels, setHasSimilarHotels] = useState(true)
  const {data: hotel} = useSelector(state => getHotelResult(state, hotelId))
  const isRefreshing = useSelector(getIsRoomsRefreshing)

  // The rooms list only carries the banner on mobile; desktop has its own slot.
  const showLoginBanner = useShouldShowLoginBanner(isMobile)

  // Height preservation for auto-refresh to prevent layout shift
  const roomsSectionRef = useRef<HTMLDivElement>(null)
  const preservedHeightRef = useRef<number>()

  const loading =
    isRoomsPollingWaitingForResults ||
    isUninitialized ||
    isLoading ||
    isFetching

  const roomsWithImages = rooms.filter(room => Boolean(room.images?.[0]?.url))

  const firstRoomImageLoaded = useIsImageloaded(
    roomsWithImages[roomsWithImages.length - 1]?.images?.[0]?.url
  )
  const hasActiveRoomFilter = useSelector(getHasActiveRoomFilter)

  const isListExpanded = useSelector(isRoomsViewExpanded)

  const roomCardEntities = useSelector(getRoomCardEntities)

  const filteredRoomsLength = roomCardEntities.length

  const roomsEntitiesToShow = roomCardEntities.slice(
    0,
    isListExpanded ? filteredRoomsLength : COMPACT_ROOMS_AMOUNT
  )

  const noAvailableRoomsWithFilters =
    !filteredRoomsLength && hasActiveRoomFilter

  const offerPriceBreakdownOverlayParams = useSelector(
    getRoomOfferPriceBreakdownOverlay
  )

  const {isPropertyEligible, isLocalVatIncluded, hasAdditionalCharges} =
    useIsraeliVatEligibility(hotelId)
  const onCloseOverlay = () => {
    dispatch(hideOverlay())
  }

  useEffect(() => {
    if (firstRoomImageLoaded.isLoaded) {
      dispatch(setRoomsFirstImageLoaded(true))
      dispatch(markAccommodationPageLoad())
    }
    return () => {
      dispatch(setRoomsFirstImageLoaded(false))
    }
  }, [firstRoomImageLoaded.isLoaded, dispatch])

  useEffect(() => {
    if (!loading) dispatch(markAccommodationPageLoad())
  }, [loading, dispatch])

  // Capture height while content is visible for use during auto-refresh to prevent layout shift
  useIsomorphicLayoutEffect(() => {
    const isShowingContent = !loading && !isRefreshing && !isRoomsFiltersLoading
    if (isShowingContent && roomsSectionRef.current) {
      preservedHeightRef.current = roomsSectionRef.current.offsetHeight
    }
  }, [loading, isRefreshing, isRoomsFiltersLoading, rooms, isListExpanded])

  const searchParameters = useSelector(getSearchParameters)
  const {checkIn, checkOut} = searchParameters || {} // They may not be synced yet

  const scrollOffset = useAccommodationPageScrollTopOffset()

  const frozenBooking = useSelector((state: RootState) =>
    searchParameters
      ? getFrozenBookingByItinerary(state, checkIn, checkOut, hotelId)
      : undefined
  )

  const frozenRoom = roomCardEntities.find(entity => {
    const {id, squashedIds = []} = entity.data as Room
    const roomIds = [id, ...squashedIds]
    return frozenBooking?.rooms?.some(room => roomIds.includes(room.id))
  })

  const shouldShowHotelCardClickersContent =
    useShouldShowHotelCardClickersContent()

  const shouldShowRoomsSectionSoldOut =
    !totalResults && !isMobile && !shouldShowHotelCardClickersContent

  const roomsStateDataId = useMemo(() => {
    if (isRoomsPollingComplete) return 'RoomSearchCompleted'

    return 'RoomSearchIdle'
  }, [isRoomsPollingComplete])

  return (
    <ScrollSection
      name={AccommodationPageNavSections.Rooms}
      scrollTopMargin={scrollOffset}
      dataId={roomsStateDataId}
    >
      <Wrapper
        data-id="roomsSection"
        isMobile={isMobile}
        ref={roomsSectionRef}
        style={
          (isRefreshing || isFetching) && preservedHeightRef.current
            ? {minHeight: preservedHeightRef.current}
            : undefined
        }
      >
        <Stack space="s500">
          <div>
            <Stack space="s500">
              <Box display="flex" flexDirection="column" gap="s100">
                <RoomsSectionTitle
                  roomsCount={filteredRoomsLength}
                  isCountLoading={loading || isRoomsFiltersLoading}
                />
                <RoomsSectionSearchInputContext hotelId={hotelId} />
              </Box>
              {isMobile && afterRoomsTitleSlot}
              {hasPriceWatchToggle && (
                <PriceWatchWrapper>
                  <PriceWatchCTAWrapper
                    hotel={hotel}
                    ctaComponent={PriceWatchToggle}
                    componentName="PriceWatchToggle"
                    searchIsCompleted={!isLoading}
                    hasAlertText={hasAlertText}
                  />
                </PriceWatchWrapper>
              )}
              <ScrollSection
                name={AccommodationPageNavSections.RoomsFilters}
                scrollTopMargin={scrollOffset}
              >
                {loading ? (
                  <FiltersSkeleton />
                ) : isMobile ? (
                  <RoomsFiltersWrapper>
                    <RoomsFilters availableFilters={availableFilters} />
                  </RoomsFiltersWrapper>
                ) : (
                  <RoomsFilters availableFilters={availableFilters} />
                )}
              </ScrollSection>
              <PointsPriceToggle id="roomsPointsPriceToggle" />
            </Stack>

            <RoomsWrapper className={isMobile && '--mobile'}>
              <Stack space="s500">
                {loading || isRoomsFiltersLoading ? (
                  <RoomsListSkeleton />
                ) : noAvailableRoomsWithFilters ? (
                  <NoFilteredRoomsResults />
                ) : shouldShowRoomsSectionSoldOut ? (
                  <>
                    <RoomsSectionSoldOut
                      handleChangeDatesClick={() => {
                        handleScrollTop()
                        showDatePickerOverlay(DatePickerType.CheckIn)
                      }}
                    />
                    {hasSimilarHotels && (
                      <>
                        <Box paddingTop="s500">
                          <Divider />
                        </Box>
                        <Box paddingTop="s600">
                          <AccommodationViewSuggestedHotels
                            onHotelsLoadingComplete={setHasSimilarHotels}
                            hotelId={hotelId}
                            hasSideMargin={false}
                          />
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {showLoginBanner && (
                      <Box paddingTop="s300">
                        <LoginBanner />
                      </Box>
                    )}
                    {frozenRoom && (
                      <>
                        <RoomCard
                          hotelId={hotelId}
                          room={frozenRoom?.data as Room}
                          isFrozen
                        />
                        <Box marginTop={isMobile ? 's300' : 's500'}>
                          <Text variant="titleL">
                            <FormattedMessageWrapper
                              id="dealFreeze.otherAvailableDeals"
                              defaultMessage="Other available deals"
                            />
                          </Text>
                        </Box>
                      </>
                    )}

                    {roomsEntitiesToShow.map((entity, idx) => (
                      <RoomOrSplitBooking
                        key={entity.id}
                        entity={entity}
                        isCheapest={idx === 0}
                        hotelId={hotelId}
                        totalResults={totalResults}
                      />
                    ))}
                  </>
                )}
              </Stack>
            </RoomsWrapper>
          </div>
          <RoomsSectionFooter isLoading={loading} />
        </Stack>
        <PriceBreakdownModal
          isOpen={Boolean(offerPriceBreakdownOverlayParams)}
          onClose={onCloseOverlay}
          prefersReducedMotion
          hasAdditionalCharges={hasAdditionalCharges}
          hotelCountryCode={hotel?.country}
          isLocalVatIncluded={isLocalVatIncluded}
          showVatDisclaimer={isPropertyEligible}
          {...offerPriceBreakdownOverlayParams}
        />
      </Wrapper>
    </ScrollSection>
  )
}
